import { contrastRatio } from '../widget/theme.js';
import { trackCtaClicked } from './analyticsTracker.js';

const escapeHtml = (value) => String(value)
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

/**
 * Read the current settings of a configurator form.
 */
const readSettings = (form) => ({
    accent: form.elements.accent.value,
    background: form.elements.background.value,
    text: form.elements.text.value,
    border: form.elements.border_enabled.checked ? form.elements.border.value : 'none',
    radius: form.elements.radius.value,
    font: form.elements.font.value,
    lang: form.elements.lang.value,
});

/**
 * Embed snippet for the given settings: the backlink plus the widget script.
 */
const buildSnippet = (calculator, settings, pageUrls, linkTexts, scriptUrl) => {
    const attributes = {
        class: 'calcimo-widget',
        href: pageUrls[settings.lang],
        'data-calculator': calculator,
        'data-lang': settings.lang,
        'data-accent': settings.accent,
        'data-background': settings.background,
        'data-text': settings.text,
        'data-border': settings.border,
        'data-radius': settings.radius,
        'data-font': settings.font,
    };
    const attributeString = Object.entries(attributes)
        .map(([name, value]) => `${name}="${escapeHtml(value)}"`)
        .join(' ');

    return `<a ${attributeString}>${escapeHtml(linkTexts[settings.lang])}</a>\n<script src="${escapeHtml(scriptUrl)}" async></script>`;
};

const applyToPreview = (anchor, settings, pageUrls, linkTexts) => {
    anchor.href = pageUrls[settings.lang];
    anchor.textContent = linkTexts[settings.lang];
    ['lang', 'accent', 'background', 'text', 'border', 'radius', 'font'].forEach((name) => {
        anchor.dataset[name] = settings[name];
    });
    window.CalcimoWidget?.render(anchor);
};

const copyText = async (text) => {
    if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);

        return;
    }

    const helper = document.createElement('textarea');
    helper.value = text;
    helper.setAttribute('readonly', '');
    helper.style.position = 'fixed';
    helper.style.opacity = '0';
    document.body.appendChild(helper);
    helper.select();
    const copied = document.execCommand('copy');
    helper.remove();
    if (!copied) {
        throw new Error('Copy failed');
    }
};

const openDonationDialog = (trigger) => {
    const dialog = document.querySelector('[data-widget-donation-dialog]');
    if (!dialog || typeof dialog.showModal !== 'function' || dialog.open) {
        return;
    }

    dialog.showModal();
    trackCtaClicked('widget_donation_dialog_opened');
    dialog.addEventListener('close', () => trigger.focus(), { once: true });
};

const initDonationDialog = () => {
    const dialog = document.querySelector('[data-widget-donation-dialog]');
    if (!dialog) {
        return;
    }

    let donationSelected = false;
    dialog.addEventListener('click', (event) => {
        if (event.target.closest('[data-analytics-cta^="donation_amount_"], [data-analytics-cta="donation_custom"]')) {
            if (!donationSelected) {
                donationSelected = true;
                trackCtaClicked('widget_donation_dialog_option_clicked');
            }
        }
    });
    dialog.addEventListener('close', () => {
        if (!donationSelected) {
            trackCtaClicked('widget_donation_dialog_dismissed');
        }
        donationSelected = false;
    });

    dialog.querySelectorAll('[data-widget-dialog-close]').forEach((button) => {
        button.addEventListener('click', () => dialog.close());
    });
    dialog.addEventListener('click', (event) => {
        if (event.target === dialog) {
            dialog.close();
        }
    });
};

const initConfigurator = (container) => {
    const form = container.querySelector('[data-widget-settings]');
    const anchor = container.querySelector('[data-widget-preview] a.calcimo-widget');
    const code = container.querySelector('[data-widget-code]');
    const copyButton = container.querySelector('[data-widget-copy]');
    const copyStatus = container.querySelector('[data-widget-copy-status]');
    const radiusOutput = container.querySelector('[data-widget-radius-output]');
    const borderColor = container.querySelector('[data-widget-border-color]');
    const contrastWarning = container.querySelector('[data-widget-contrast-warning]');
    const calculator = container.dataset.calculator;
    const pageUrls = JSON.parse(container.dataset.pageUrls);
    const linkTexts = JSON.parse(container.dataset.linkTexts);
    const scriptUrl = container.dataset.scriptUrl;
    let snippet = '';

    const update = () => {
        const settings = readSettings(form);
        radiusOutput.textContent = settings.radius;
        borderColor.hidden = settings.border === 'none';
        contrastWarning.hidden = contrastRatio(settings.text, settings.background) >= 4.5;
        snippet = buildSnippet(calculator, settings, pageUrls, linkTexts, scriptUrl);
        code.textContent = snippet;
        applyToPreview(anchor, settings, pageUrls, linkTexts);
    };

    form.addEventListener('input', update);
    form.addEventListener('change', update);
    form.addEventListener('reset', () => window.setTimeout(update));
    document.addEventListener('calcimo-widget:ready', update, { once: true });
    update();

    copyButton.addEventListener('click', async () => {
        try {
            await copyText(snippet);
            copyStatus.textContent = copyButton.dataset.copiedLabel;
            openDonationDialog(copyButton);
        } catch (error) {
            copyStatus.textContent = copyButton.dataset.errorLabel;
        }
    });
};

const initWidgetSelection = () => {
    const selectors = [...document.querySelectorAll('[data-widget-select]')];
    const sections = [...document.querySelectorAll('[data-widget-section]')];
    if (selectors.length === 0 || sections.length === 0) {
        return;
    }

    const keys = new Set(sections.map((section) => section.dataset.widgetSection));
    const keyFromHash = () => {
        const key = window.location.hash.replace(/^#widget-/, '');

        return keys.has(key) ? key : sections[0].dataset.widgetSection;
    };
    const activate = (key, scroll = false) => {
        sections.forEach((section) => {
            section.hidden = section.dataset.widgetSection !== key;
        });
        selectors.forEach((selector) => {
            const active = selector.dataset.widgetSelect === key;
            selector.classList.toggle('btn-primary', active);
            selector.classList.toggle('btn-outline-primary', !active);
            selector.setAttribute('aria-expanded', String(active));
        });
        window.CalcimoWidget?.scan();

        if (scroll) {
            const section = sections.find((item) => item.dataset.widgetSection === key);
            section?.scrollIntoView({ block: 'start' });
            section?.focus({ preventScroll: true });
        }
    };

    selectors.forEach((selector) => {
        selector.addEventListener('click', (event) => {
            event.preventDefault();
            const key = selector.dataset.widgetSelect;
            window.history.pushState(null, '', `#widget-${key}`);
            activate(key, true);
        });
    });
    window.addEventListener('popstate', () => activate(keyFromHash(), true));
    window.addEventListener('hashchange', () => activate(keyFromHash(), true));
    activate(keyFromHash(), window.location.hash.startsWith('#widget-'));
};

export const initWidgetConfigurators = () => {
    const containers = document.querySelectorAll('[data-widget-configurator]');
    if (containers.length === 0) {
        return;
    }

    initWidgetSelection();
    containers.forEach(initConfigurator);
    initDonationDialog();
};
